/**
 * The cells of one draft line, shared by the table (laptop) and the cards (phone and the
 * Outlook pane): the status badge with its icon, the customer's words with their source
 * icon, what I2Q proposes with its basis icon, quantity, price with its band icon, lead
 * time with its icon, and the one row action. One renderer per cell, so a sales person
 * reads the same icon on every surface.
 *
 * Icons with a word next to them are decorative; an icon that stands alone (the source,
 * the basis, a quantity issue) carries its label. `longFigures` (the table's "Show
 * details") adds the long sentences to the cells: the list price, the band and the
 * margin, the lead time source, the basis word. The table never changes shape for it.
 */

import { Check, ChevronDown, Icon, PackageSearch } from "@norican/icons";
import {
  Badge,
  Button,
  cn,
  type DataGridColumn,
  EditableNumberCell,
  SaveIndicator,
  Typography,
  useSaveIndicator,
} from "@norican/react";
import { type KeyboardEvent, type ReactNode, useRef, useState } from "react";

import { DRAFT_LINE_ATTR, GRID_CELL_ATTR } from "@/features/case-review/draft-focus";
import { DraftLineDetails } from "@/features/case-review/draft-line-details";
import { type DraftRow } from "@/features/case-review/draft-line-groups";
import { lineProposalsOf, proposalIsRowItem } from "@/features/case-review/investigation";
import {
  AlternativeNote,
  ProvisionalChip,
  provisionalFigure,
} from "@/features/case-review/investigation-panel";
import { isAddedLine, QTY_WHOLE_ERROR } from "@/features/case-review/line-corrections";
import { hasLineMenu, LineMenu } from "@/features/case-review/line-menu";
import { effectiveItem, lineOutcome } from "@/features/case-review/line-semantics";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import { personName } from "@/lib/api";
import { isScanSource } from "@/lib/data";
import {
  BASIS_ICON,
  BASIS_LABEL,
  type BasisKind,
  GROUP_ICON,
  GROUP_TONE,
  INVESTIGATION_ICON,
  LEAD_ICON,
  type LeadState,
  MANUAL_PRICE_ICON,
  PRICE_ICON,
  type PriceBand,
  QTY_DEFAULT_ICON,
  QTY_ISSUE_ICON,
  SOURCE_ICON,
  SOURCE_LABEL,
  type SourceKind,
  STATUS_ICON,
  STATUS_TONE,
} from "@/lib/line-icons";
import { priceSetByText } from "@/lib/line-price";
import { NOT_APPLYING_TAG } from "@/lib/local-price";
import { type LinePrice, type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import {
  ACTION_LABELS,
  DRAFT_CELL_WORDS,
  DRAFT_COLUMNS,
  type DraftGroup,
  draftGroupSentence,
  hsnChipText,
  LEAD_FIGURE_LABELS,
  LEAD_GAP_WORDS,
  LEAD_ORIGIN_WORDS,
  LINE_STATUS_WORDS,
  mismatchChipText,
  otherEntityChipText,
  PADDED_NUMBER_WORD,
  PICTURE_WORDS,
  PRICE_APPROVAL_WORDS,
  PRICE_GAP_WORDS,
  type PriceGap,
  QUANTITY_WORDS,
} from "@/lib/sales-flow";
import {
  formatAmount,
  formatDay,
  formatQty,
  INVESTIGATION_WORDS,
  isHistoricLead,
  isItemMasterLead,
  itemStatusTag,
  leadOriginText,
  leadTimeSentence,
  marginTag,
  priceGapOf,
  priceSentence,
  provisionalPriceSentence,
  twinDecisionRequest,
  twinDecisionSentence,
} from "@/lib/work-labels";

// ─── the figures ─────────────────────────────────────────────────────────────

/** "4 pcs" from the quantity and the unit; empty without a quantity. */
export function qtyText(line: Pick<QuoteLine, "qty" | "unit">): string {
  if (line.qty == null) {
    return "";
  }
  return [formatQty(line.qty), (line.unit ?? "").trim()].filter(Boolean).join(" ");
}

/** "412.50 GBP" for a found price that applies; empty otherwise. */
export function priceText(
  price: QuoteLine["price"],
  note: { greyed: boolean } | null | undefined,
): string {
  if (!price || price.status !== "found" || note?.greyed) {
    return "";
  }
  return [formatAmount(price.unitPrice), price.currency].filter(Boolean).join(" ");
}

/** "12 days" when the lead time is known; empty otherwise. */
export function leadText(price: QuoteLine["price"]): string {
  const days = price?.leadDays;
  if (typeof days !== "number" || !Number.isFinite(days)) {
    return "";
  }
  return `${days} ${days === 1 ? "day" : "days"}`;
}

// ─── what each icon stands for on a line ─────────────────────────────────────

/** Where the customer's words come from: a photo, a scan, an attachment, the thread, the mail. */
export function sourceKindOf(
  line: Pick<QuoteLine, "source" | "fromPhoto" | "reasonCode">,
): SourceKind {
  if (line.fromPhoto || line.reasonCode === "photo_read") {
    return "photo";
  }
  if (isScanSource(line.source) || line.reasonCode === "scan_read") {
    return "scan";
  }
  if (line.source?.kind === "attachment") {
    return "attachment";
  }
  // An earlier message of the thread: a carried request, a number written only in the
  // quoted history, or a line read in the quoted or forwarded part because the newest part
  // named none (bug hunt 14 Oct 2026, Q-02).
  if (
    line.reasonCode === "thread_carry" ||
    line.source?.kind === "thread" ||
    (line.source?.kind === "mail" && line.source.quoted === true)
  ) {
    return "thread";
  }
  return "mail";
}

/**
 * How the proposed item was found: a person's decision first, then the match basis;
 * a model guess or a similar description reads as sparkles. Null when nothing is known.
 */
export function basisKindOf(line: QuoteLine): BasisKind | null {
  const outcome = lineOutcome(line);
  if (outcome.kind === "decided" && !outcome.byI2q) {
    return "person";
  }
  const basis = line.matchBasis ?? null;
  if (basis && Object.hasOwn(BASIS_ICON, basis)) {
    return basis as BasisKind;
  }
  if (line.reasonCode === "similar_description") {
    return "similar";
  }
  return null;
}

/** The band of the row's price: in band, below the margin, or nothing to quote. */
export function priceBandOf(
  price: QuoteLine["price"],
  note: { greyed: boolean } | null | undefined,
): PriceBand {
  if (!price || price.status !== "found" || note?.greyed) {
    return "none";
  }
  return price.margin === "needs_approval" ? "below_margin" : "in_band";
}

/**
 * Why the row has no price to quote (sales review of 12 Oct 2026, P-01): the gap of its
 * price status, or "applies_not" for a local price another company sets (sim360 P33).
 * Null when the row has a price that applies. `customerToFind`: nobody is suggested, so
 * the price waits for "Find customer", not for a confirmation.
 */
export function priceGapOfRow(
  price: QuoteLine["price"],
  note: { greyed: boolean } | null | undefined,
  customerToFind = false,
): PriceGap | "applies_not" | null {
  if (priceBandOf(price, note) !== "none") {
    return null;
  }
  return note?.greyed ? "applies_not" : priceGapOf(price?.status, customerToFind);
}

/** The words of the price cell for a gap: the reason, never a bare "no price". */
export function priceGapText(gap: PriceGap | "applies_not"): string {
  return gap === "applies_not" ? NOT_APPLYING_TAG : PRICE_GAP_WORDS[gap];
}

/**
 * The state of the lead cell (sales review of 12 Oct 2026, P-02): a lead time F&O holds
 * today, one from an earlier quotation or mail offer (context, never a promise), none in
 * F&O after a lookup, or not looked up yet.
 */
export function leadStateOf(price: QuoteLine["price"]): LeadState {
  if (leadText(price)) {
    return isHistoricLead(price?.leadSource, price?.leadStatus) ? "historic" : "known";
  }
  return price?.leadStatus === "none_in_fo" ? "none" : "not_looked_up";
}

/**
 * "From quotation SQ0990777 on 5 Oct 2026" under a historic lead time, "Item master: sales
 * lead time" under one from the item master; null otherwise.
 */
export function leadOriginOf(price: QuoteLine["price"]): string | null {
  // A lead time of the item master always names its source (ease round, contract 2.4):
  // it is a master data figure, and the reply says "about, to be confirmed".
  if (leadText(price) && isItemMasterLead(price?.leadSource)) {
    return leadOriginText(price?.leadSource);
  }
  if (leadStateOf(price) !== "historic") {
    return null;
  }
  return leadOriginText(price?.leadSource) ?? LEAD_ORIGIN_WORDS.quotationUnknown;
}

/** "exact number" from "exact_number": the basis word of the long figures. */
export function basisWord(kind: BasisKind): string {
  if (kind === "padded_number") {
    return PADDED_NUMBER_WORD;
  }
  return kind === "person" ? "decided by a person" : kind.replace(/_/g, " ");
}

/** One chip of a line (hunt round 2): what the row must say without "Show details". */
export interface LineChip {
  key: string;
  label: string;
  tone: "info" | "warning" | "neutral";
}

/**
 * The chips of a line, shown on the row itself (hunt round 2): the item of another entity
 * (decision 5), the HSN code of an India line (decision 10), an item that does not fit
 * the row (Q-09, Q-10), a scan line one reading gave, and the phase-out or blocked state
 * of the item (Q-27: it sat behind "Show details").
 */
export function lineChipsOf(
  line: Pick<
    QuoteLine,
    "otherEntity" | "hsnCode" | "hsnStatus" | "mismatch" | "readOnce" | "price" | "matchedItemId"
  >,
): LineChip[] {
  const chips: LineChip[] = [];
  if (!line.matchedItemId) {
    for (const entry of line.otherEntity ?? []) {
      chips.push({
        key: `other:${entry.entity}:${entry.item}`,
        label: otherEntityChipText(entry),
        tone: "info",
      });
    }
  }
  const mismatch = mismatchChipText(line.mismatch);
  if (mismatch) {
    chips.push({ key: "mismatch", label: mismatch, tone: "warning" });
  }
  if (line.readOnce) {
    chips.push({ key: "read-once", label: PICTURE_WORDS.readOnce, tone: "warning" });
  }
  const status = itemStatusTag(line.price?.itemStatus, line.price?.replacement);
  if (status) {
    chips.push({ key: "item-status", label: status.label, tone: "warning" });
  }
  const hsn = hsnChipText(line.hsnCode, line.hsnStatus);
  if (hsn) {
    chips.push({
      key: "hsn",
      label: hsn,
      tone: line.hsnStatus === "present" ? "neutral" : "warning",
    });
  }
  return chips;
}

/** The chips of a line under what I2Q proposes; nothing without a chip. */
function LineChips({ line }: { line: QuoteLine }) {
  const chips = lineChipsOf(line);
  if (chips.length === 0) {
    return null;
  }
  return (
    <span className="flex flex-wrap items-center gap-1 pt-0.5" data-line-chips>
      {chips.map((chip) => (
        <Badge
          key={chip.key}
          tone={chip.tone}
          variant="subtle"
          size="xs"
          className="max-w-full"
          data-line-chip={chip.key}
        >
          <span className="break-words whitespace-normal">{chip.label}</span>
        </Badge>
      ))}
    </span>
  );
}

// ─── the context of a render ─────────────────────────────────────────────────

export interface DraftCellContext {
  detail: QuoteCaseDetail;
  caseArea: string | null;
  maySeeMargin: boolean;
  /** A customer is suggested but not confirmed: a line price waits for it. */
  customerSuggested: boolean;
  /**
   * Nobody is suggested (the customer line says "Find customer"): a line price waits for
   * the search, so the cell and the long sentence do not speak of a confirmation.
   */
  customerToFind: boolean;
  /** sim360 P33: a local price another company sets is greyed out. */
  priceNote: (line: QuoteLine) => { greyed: boolean; text: string } | null;
  /** The long sentences in the cells (the table's "Show details"). */
  longFigures: boolean;
  /** The viewer may approve a price below the limit: the row menu offers it on a held line. */
  mayApprove: boolean;
  writes: CaseWrites;
  onPick: (lineId: string) => void;
  /** The row menu's "Set price" / "Change price": opens the SetPriceDialog for the line. */
  onSetPrice: (lineId: string) => void;
  /** The row menu's "Correct quantity": opens the LineCorrectionDialog for the line (U-01). */
  onCorrectQty: (lineId: string) => void;
  /** The customer's words were clicked: the mail marks them. */
  onChooseLine: (lineId: string) => void;
}

/** Whether the row's own button may act: the case is open and the line has a key. */
export function canActOn(ctx: Pick<DraftCellContext, "detail">, row: DraftRow): boolean {
  const workOpen = (ctx.detail.workStatus ?? "open") === "open";
  return (
    workOpen &&
    Boolean(row.line.lineKey) &&
    !isAddedLine(row.line) &&
    row.lineIndex >= 0 &&
    !row.line.decision
  );
}

/** A colleague's decision on the same line of the first mail (P18), while the line is open. */
export function twinOf(row: DraftRow) {
  const { line } = row;
  return line.decision || line.requestAction === "remove" ? null : (line.twinDecision ?? null);
}

// ─── the cells ───────────────────────────────────────────────────────────────

/**
 * The status badge: the icon, the word, and (in the table) the chevron the DS rotates
 * when the detail row is open. Inside the table the cell itself is the drill button, so
 * nothing interactive sits in here.
 */
export function StatusBadge({
  row,
  chevron = false,
  marks = true,
}: {
  row: DraftRow;
  chevron?: boolean;
  /** The row's data attributes: on the badge in the table, on the card's `<li>` in the list. */
  marks?: boolean;
}) {
  const { word } = row;
  const tone = STATUS_TONE[word];
  return (
    <Badge
      tone={tone ?? "neutral"}
      variant={word === LINE_STATUS_WORDS.noItem ? "outline" : "subtle"}
      size="sm"
      className="max-w-full"
      data-draft-line={marks ? row.line.id : undefined}
      data-line-word={marks ? word : undefined}
    >
      <span className="inline-flex items-center gap-1">
        <Icon icon={STATUS_ICON[word]} size="xs" />
        {word}
        {chevron && (
          <Icon
            icon={ChevronDown}
            size="xs"
            className="transition-transform [[aria-expanded=true]_&]:rotate-180"
          />
        )}
      </span>
    </Badge>
  );
}

/**
 * The customer's words; a click marks them in the mail. In the table ("cell") they sit
 * on one line with the source icon and the full text as the title; on a card they wrap
 * and the source icon is on the card's first line already.
 */
export function AskedCell({
  row,
  ctx,
  form = "cell",
}: {
  row: DraftRow;
  ctx: DraftCellContext;
  form?: "cell" | "card";
}) {
  const kind = sourceKindOf(row.line);
  const text = row.line.enquiryText;
  return (
    <Button
      size="sm"
      variant="ghost"
      tone="neutral"
      className="h-auto w-full max-w-full min-w-0 justify-start px-1 py-0.5 text-left font-normal whitespace-normal"
      title={text}
      aria-label={`Line ${row.position}: ${text}. Mark it in the mail`}
      onClick={() => ctx.onChooseLine(row.line.id)}
    >
      {form === "cell" && (
        <Icon icon={SOURCE_ICON[kind]} size="xs" label={SOURCE_LABEL[kind]} className="shrink-0" />
      )}
      <span className={cn("text-fg min-w-0 text-sm", form === "cell" ? "truncate" : "break-words")}>
        {text}
      </span>
    </Button>
  );
}

/** What I2Q proposes (or a person picked), with the basis icon; "No item" when nothing is. */
export function ProposedCell({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const shown = effectiveItem(row.line);
  if (!shown.item) {
    // "No item" is a decision, so the words belong to that row only; an open line
    // without a proposal shows only its chips (its button says "Pick item"): the item
    // of another entity, for one (hunt round 2, decision 5).
    return row.word === LINE_STATUS_WORDS.noItem ? (
      <span className="text-fg-muted inline-flex min-w-0 items-center gap-1 text-sm">
        <Icon icon={STATUS_ICON[LINE_STATUS_WORDS.noItem]} size="xs" />
        {ACTION_LABELS.noItem}
      </span>
    ) : (
      <LineChips line={row.line} />
    );
  }
  const basis = basisKindOf(row.line);
  return (
    <span className="flex min-w-0 flex-col">
      <span
        className="inline-flex min-w-0 items-center gap-1 text-sm"
        title={shown.name ?? shown.item}
      >
        {basis && (
          <Icon
            icon={BASIS_ICON[basis]}
            size="xs"
            label={BASIS_LABEL[basis]}
            className="shrink-0"
          />
        )}
        <span className="text-fg font-mono font-semibold">{shown.item}</span>
        {shown.name && <span className="text-fg-muted min-w-0 truncate">{shown.name}</span>}
      </span>
      {ctx.longFigures && basis && (
        <span className="text-fg-muted text-xs">{basisWord(basis)}</span>
      )}
      <LineChips line={row.line} />
      {/* The item is a proposal of the deeper search: the row says so, "Why" is behind
          the chevron. An alternative stands under the item and changes nothing. */}
      {!row.line.decision &&
        lineProposalsOf(ctx.detail.investigation, row.line.lineKey).some((proposal) =>
          proposalIsRowItem(proposal, row.line),
        ) && (
          <span className="text-fg-muted inline-flex items-center gap-1 text-xs" data-row-proposal>
            <Icon icon={INVESTIGATION_ICON.proposal} size="xs" className="shrink-0" />
            {INVESTIGATION_WORDS.proposed}
          </span>
        )}
      {!row.line.decision && <AlternativeNote detail={ctx.detail} line={row.line} />}
    </span>
  );
}

/**
 * Whether the Qty cell reads in the caution tone: an issue, or no quantity at all (U-05).
 * A row decided "no item" is not quoted, so its missing quantity is said without the tone.
 */
export function qtyNeedsLook(
  line: Pick<QuoteLine, "qty" | "qtyIssue">,
  word?: DraftRow["word"],
): boolean {
  return Boolean(line.qtyIssue) || (line.qty == null && word !== LINE_STATUS_WORDS.noItem);
}

/**
 * The mark on a quantity the customer wrote in an earlier message of the thread, not in the
 * newest mail (review of the hunt branch, F5): the earlier-mail icon of the one icon map,
 * with "Quantity from an earlier mail" as its label and tooltip.
 */
export function QtyEarlierMailMark() {
  return (
    <span
      className="inline-flex shrink-0 items-center"
      title={QUANTITY_WORDS.earlierMail}
      data-qty-earlier-mail
    >
      <Icon
        icon={BASIS_ICON.earlier_mail}
        size="xs"
        tone="info"
        label={QUANTITY_WORDS.earlierMail}
      />
    </span>
  );
}

/**
 * The mark on a quantity the mail did not give (product owner, 14 Oct 2026): the
 * asterisk of the one icon map, with "Quantity not given, 1 used" as its label and
 * tooltip. The quantity next to it is the 1 that I2Q prices and quotes.
 */
export function QtyDefaultMark() {
  return (
    <span
      className="inline-flex shrink-0 items-center"
      title={QUANTITY_WORDS.defaultUsed}
      data-qty-default
    >
      <Icon icon={QTY_DEFAULT_ICON} size="xs" tone="info" label={QUANTITY_WORDS.defaultUsed} />
    </span>
  );
}

/**
 * The one mark of a quantity no person set (merge of 14 Oct 2026). Precedence: a quantity
 * a person set, then the mail or a document (no mark), then an earlier mail, then the
 * default 1. A line shows at most one mark.
 */
export function qtySourceMark(
  line: Pick<QuoteLine, "override" | "qtyEarlierMail" | "qtyDefault">,
): "earlierMail" | "default" | null {
  if (qtyCorrected(line)) {
    return null;
  }
  if (line.qtyEarlierMail) {
    return "earlierMail";
  }
  if (line.qtyDefault) {
    return "default";
  }
  return null;
}

/** The mark {@link qtySourceMark} chose, or nothing. */
function QtySourceMark({ mark }: { mark: ReturnType<typeof qtySourceMark> }) {
  if (mark === "earlierMail") {
    return <QtyEarlierMailMark />;
  }
  if (mark === "default") {
    return <QtyDefaultMark />;
  }
  return null;
}

/**
 * The quantity, with the alert icon when the quantity needs a look. Without a quantity
 * the cell says so in words (sales review of 12 Oct 2026, U-05: 13 lines showed an empty
 * cell, and the row menu now offers "Correct quantity" for them). A quantity from an
 * earlier mail of the thread, or the default 1 of a line no source gave a quantity for
 * (14 Oct 2026), carries its mark: at most one, see {@link qtySourceMark}.
 */
export function QuantityCell({ row, ctx }: { row: DraftRow; ctx?: DraftCellContext }) {
  // On an open case the quantity of an engine line is typed in the cell (ease round,
  // 13 Oct 2026: one dialog per line cost three clicks a line). A closed case and a line
  // sales added keep the plain text.
  if (ctx && hasLineMenu(ctx, row)) {
    return <EditableQuantity row={row} ctx={ctx} />;
  }
  const text = qtyText(row.line);
  if (row.line.qty == null) {
    return (
      <span
        className={cn(
          "text-xs whitespace-normal",
          qtyNeedsLook(row.line, row.word) ? "text-warning-fg" : "text-fg-subtle",
        )}
        data-qty-missing
      >
        {DRAFT_CELL_WORDS.qtyMissing}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      {row.line.qtyIssue && (
        <Icon
          icon={QTY_ISSUE_ICON}
          size="xs"
          tone="warning"
          label={DRAFT_CELL_WORDS.qtyIssue}
          className="shrink-0"
        />
      )}
      <QtySourceMark mark={qtySourceMark(row.line)} />
      {text}
    </span>
  );
}

/** The mark of the wrapper of an editable quantity cell: the keys and the tests find it by this. */
export const QTY_EDIT_ATTR = "data-qty-edit";

/** True when sales corrected the quantity of the line (the cell carries the mark). */
export function qtyCorrected(line: Pick<QuoteLine, "override">): boolean {
  return line.override?.kind === "edit" && line.override.qty != null;
}

/**
 * What a typed quantity is: a whole number above 0 to save, or the reason it is refused.
 * I2Q holds whole quantities (the server refuses a fraction), and an empty field is not
 * a quantity: it sends nothing.
 */
export function typedQuantity(
  next: number | null,
): { kind: "save"; qty: number } | { kind: "nothing" } | { kind: "refused"; error: string } {
  if (next === null) {
    return { kind: "nothing" };
  }
  if (!Number.isInteger(next) || next <= 0 || next > 1_000_000) {
    return { kind: "refused", error: QTY_WHOLE_ERROR };
  }
  return { kind: "save", qty: next };
}

/**
 * The quantity as a field in the cell: click (or the key `q`), type, Enter saves, Tab
 * saves and goes to the quantity of the next row, Escape cancels. The DS cell selects the
 * old value on focus, saves on Enter and on leaving the field, and sends nothing for the
 * same value. This wrapper adds the Tab path through the rows and puts the focus back on
 * the cell after Enter and Escape, so the keys go on.
 */
function EditableQuantity({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const { line, lineIndex, word } = row;
  const [saved, markSaved] = useSaveIndicator();
  // Why the last typed value was refused, with the quantity the line had then: the
  // sentence goes away as soon as the line has another quantity (set from elsewhere).
  const [refusal, setRefusal] = useState<{ text: string; qty: number | null } | null>(null);
  const error = refusal && refusal.qty === (line.qty ?? null) ? refusal.text : null;
  const setError = (text: string | null) =>
    setRefusal(text === null ? null : { text, qty: line.qty ?? null });
  // The quantity on its way: a second commit of the same value (Enter, then the field
  // losing the focus) sends nothing.
  const sending = useRef<number | null>(null);
  const corrected = qtyCorrected(line);
  // A quantity from an earlier mail of the thread (F5) or the default 1 (14 Oct 2026):
  // typed over like any quantity, a 1 typed saves it as the person's own. At most one mark.
  const mark = qtySourceMark(line);
  const markWord =
    mark === "earlierMail"
      ? QUANTITY_WORDS.earlierMail
      : mark === "default"
        ? QUANTITY_WORDS.defaultUsed
        : null;

  const commit = (next: number | null) => {
    const typed = typedQuantity(next);
    if (typed.kind === "nothing") {
      setError(null);
      return;
    }
    if (typed.kind === "refused") {
      setError(typed.error);
      return;
    }
    setError(null);
    if (sending.current === typed.qty || !line.lineKey) {
      return;
    }
    sending.current = typed.qty;
    // Free the guard when the write is over, saved or not.
    window.setTimeout(() => {
      sending.current = null;
    }, 1_000);
    ctx.writes.setQuantity(lineIndex, line.lineKey, typed.qty, undefined, markSaved);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    const field = event.target as HTMLElement;
    if (field.tagName !== "INPUT") {
      return;
    }
    const wrapper = event.currentTarget;
    if (event.key === "Tab") {
      const cells = Array.from(document.querySelectorAll<HTMLElement>(`[${QTY_EDIT_ATTR}]`));
      const next = cells[cells.indexOf(wrapper) + (event.shiftKey ? -1 : 1)];
      if (!next) {
        return; // The last row: Tab leaves the draft as the browser does it.
      }
      event.preventDefault();
      field.blur(); // The DS cell saves when its field loses the focus.
      next.querySelector<HTMLElement>("button")?.click();
    } else if (event.key === "Enter" || event.key === "Escape") {
      // The DS cell closes its field. In the table the grid cell takes the focus, on a
      // card the card does, so the arrow keys, Enter and `q` go on from the row (review
      // of the ease round, 13 Oct 2026: on a card the quantity kept the focus, and every
      // next `q` wrote to the same line).
      window.requestAnimationFrame(() => {
        const cell = wrapper.closest<HTMLElement>(`td[${GRID_CELL_ATTR}]`);
        const card = wrapper.closest<HTMLElement>(`li[${DRAFT_LINE_ATTR}]`);
        (cell ?? card ?? wrapper.querySelector<HTMLElement>("button"))?.focus();
      });
    }
  };

  return (
    <span className="flex min-w-0 flex-col items-end gap-0.5">
      <span
        className="inline-flex max-w-full items-center justify-end gap-1"
        onKeyDown={onKeyDown}
        {...{ [QTY_EDIT_ATTR]: line.id }}
      >
        {line.qtyIssue && (
          <Icon
            icon={QTY_ISSUE_ICON}
            size="xs"
            tone="warning"
            label={DRAFT_CELL_WORDS.qtyIssue}
            className="shrink-0"
          />
        )}
        <QtySourceMark mark={mark} />
        <EditableNumberCell
          value={line.qty ?? null}
          onCommit={commit}
          format={(value) => qtyText({ qty: value, unit: line.unit })}
          editable
          overridden={corrected}
          tone={qtyNeedsLook(line, word) ? "caution" : "neutral"}
          title={
            corrected
              ? `${QUANTITY_WORDS.corrected}. ${QUANTITY_WORDS.inlineHint}`
              : markWord
                ? `${markWord}. ${QUANTITY_WORDS.inlineHint}`
                : QUANTITY_WORDS.inlineHint
          }
          aria-label={`Quantity of line ${row.position}: ${qtyText(line) || DRAFT_CELL_WORDS.qtyMissing}${markWord ? ` (${markWord})` : ""}`}
          className="tabular-nums"
        />
        <SaveIndicator saved={saved} />
      </span>
      {corrected && <span className="sr-only">{QUANTITY_WORDS.corrected}</span>}
      {line.qty == null && (
        // The cell still says it in words (U-05); the field is the dash above them.
        <span
          className={cn(
            "text-xs whitespace-normal",
            qtyNeedsLook(line, word) ? "text-warning-fg" : "text-fg-subtle",
          )}
          data-qty-missing
        >
          {DRAFT_CELL_WORDS.qtyMissing}
        </span>
      )}
      {error && (
        <span className="text-danger-fg text-xs whitespace-normal" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

/** The tone of the person-with-a-check on a price a person set: by its band and its margin check. */
export function manualPriceTone(
  price: Pick<LinePrice, "margin" | "marginNote">,
): "success" | "warning" | undefined {
  if (price.margin === "needs_approval") {
    return "warning";
  }
  return price.margin === "ok" && !price.marginNote ? "success" : undefined;
}

/**
 * The price with its band icon; the long figures add the list price, the band and the
 * margin. A price a person set (round 4) carries the person-with-a-check instead of the
 * band icon, in the tone of its band, and names who set it in its title. Without a price
 * the cell says why in words (sales review of 12 Oct 2026, P-01: 92 lines read "no
 * price" and hid the reason): the customer first, the quantity first, an item of another
 * catalogue, no item yet, no price or no valid price in F&O, several currencies, or not
 * looked up yet.
 */
export function PriceCell({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const { price } = row.line;
  const note = ctx.priceNote(row.line);
  const band = priceBandOf(price, note);
  const figure = priceText(price, note);
  const gap = priceGapOfRow(price, note, ctx.customerToFind);
  const gapText = gap ? priceGapText(gap) : null;
  const manual = Boolean(price && price.source === "sales" && band !== "none");
  const long =
    ctx.longFigures && price
      ? priceSentence({
          ...price,
          customerSuggested: ctx.customerSuggested && !ctx.customerToFind,
        })
      : null;
  // The long sentence never repeats the words the cell already says.
  const sentence = long && long !== gapText ? long : null;
  const margin =
    ctx.longFigures && ctx.maySeeMargin && band !== "none" ? marginTag(price?.margin) : null;
  // The phase-out or blocked state of the item is a chip of the row now (hunt round 2,
  // Q-27: it sat behind "Show details"), see `lineChipsOf`.
  // A provisional list price for the SUGGESTED account (the second pass of the deeper
  // search): shown only while the line has no price to quote, with its chip, its account
  // and its price group. It never joins a total and never replaces the band icon.
  const provisional = band === "none" ? (row.line.provisional ?? null) : null;
  const provisionalText = provisionalFigure(provisional);
  // A manager approved this price as the line is now: the cell names who and when.
  const approved =
    price?.approval === "approved"
      ? PRICE_APPROVAL_WORDS.approvedBy(
          personName(price.approvedBy) || "a manager",
          formatDay(price.approvedAt),
        )
      : null;
  // A click on the price of a line with an item opens "Set price", as the key `p` does.
  const canSetPrice = hasLineMenu(ctx, row) && Boolean(effectiveItem(row.line).item);
  const figureNode = (
    <span
      className={cn(
        "inline-flex gap-1",
        gap ? "items-start text-right text-xs whitespace-normal" : "items-center tabular-nums",
      )}
      data-price-band={band === "below_margin" ? "off" : undefined}
      data-price-source={manual ? "sales" : undefined}
      data-price-gap={gap ?? undefined}
      data-price-approval={price?.approval ?? undefined}
      title={
        approved ??
        (manual && price
          ? priceSetByText(price)
          : band === "none"
            ? (note?.text ?? undefined)
            : undefined)
      }
    >
      <Icon
        icon={manual ? MANUAL_PRICE_ICON : PRICE_ICON[band]}
        size="xs"
        tone={
          // An approved price keeps its icon and loses the warning tone: the hold is gone.
          approved
            ? undefined
            : manual && price
              ? manualPriceTone(price)
              : band === "in_band"
                ? "success"
                : band === "below_margin"
                  ? "warning"
                  : undefined
        }
        label={
          manual
            ? "Set by a person"
            : band === "below_margin"
              ? "Below margin"
              : band === "in_band"
                ? "In band"
                : undefined
        }
        className={cn("shrink-0", gap && "mt-0.5")}
      />
      {gapText ? <span className="min-w-0 break-words">{gapText}</span> : figure}
    </span>
  );
  return (
    <span className="flex min-w-0 flex-col items-end">
      {canSetPrice ? (
        <Button
          size="sm"
          variant="ghost"
          tone="neutral"
          className="h-auto max-w-full min-w-0 px-1 py-0.5 font-normal whitespace-normal"
          aria-label={`${manual ? ACTION_LABELS.changePrice : ACTION_LABELS.setPrice}: line ${row.position}`}
          onClick={() => ctx.onSetPrice(row.line.id)}
          data-price-open={row.line.id}
        >
          {figureNode}
        </Button>
      ) : (
        figureNode
      )}
      {approved && ctx.longFigures && (
        <span className="text-fg-muted text-right text-xs break-words whitespace-normal">
          {approved}
        </span>
      )}
      {provisional && provisionalText && (
        <span
          className="flex flex-col items-end gap-0.5 text-right text-xs whitespace-normal"
          data-provisional-price={provisional.account}
        >
          <span className="inline-flex flex-wrap items-center justify-end gap-1">
            <span className="text-fg tabular-nums">{provisionalText}</span>
            <ProvisionalChip />
          </span>
          <span className="text-fg-muted break-words">
            {provisionalPriceSentence(provisional.account, provisional.group)}
          </span>
        </span>
      )}
      {sentence && (
        <span className="text-fg-muted text-right text-xs break-words whitespace-normal">
          {sentence}
        </span>
      )}
      {note?.text && ctx.longFigures && (
        <span className="text-fg-muted text-right text-xs break-words whitespace-normal">
          {note.text}
        </span>
      )}
      {margin && (
        <span className="text-fg-muted text-right text-xs break-words whitespace-normal">
          {[margin.label, price?.marginBandText, price?.marginApprover].filter(Boolean).join(". ")}
        </span>
      )}
    </span>
  );
}

/**
 * The lead time with its icon (sales review of 12 Oct 2026, P-02: one "not fetched" stood
 * for three states). A lead time F&O holds today reads as before. One from an earlier
 * quotation or mail offer carries its own icon and, always, where it comes from ("From
 * quotation SQ0990777 on 5 Oct 2026"): context, never a promise. Without a lead time the
 * cell says "No lead time in F&O" after a lookup and "Not looked up yet" before one. The
 * long figures add the source of a current lead time.
 *
 * A lead time from the item master (switch `RFQ_LEAD_ITEM_MASTER`) keeps the clock of a
 * known lead time and always names its source under the figure. The long figures of the
 * table, and the card (`form="card"`), add what the reply says: "In the reply: about 21
 * days, to be confirmed." The icon has its own label, so it never reads as a firm F&O
 * lead time.
 */
export function LeadCell({
  row,
  ctx,
  form = "table",
}: {
  row: DraftRow;
  ctx: DraftCellContext;
  form?: "table" | "card";
}) {
  const { price } = row.line;
  const state = leadStateOf(price);
  const origin = leadOriginOf(price);
  const fromItemMaster = state === "known" && isItemMasterLead(price?.leadSource);
  const aboutDays = fromItemMaster && typeof price?.leadDays === "number" ? price.leadDays : null;
  const gapText =
    state === "none"
      ? LEAD_GAP_WORDS.none_in_fo
      : state === "not_looked_up"
        ? LEAD_GAP_WORDS.not_looked_up
        : null;
  return (
    <span className="flex min-w-0 flex-col" data-lead-state={state}>
      <span
        className={cn(
          "inline-flex gap-1",
          gapText ? "items-start text-xs whitespace-normal" : "items-center tabular-nums",
        )}
      >
        <Icon
          icon={LEAD_ICON[state]}
          size="xs"
          label={
            fromItemMaster
              ? LEAD_FIGURE_LABELS.itemMaster
              : state === "known" || state === "historic"
                ? LEAD_FIGURE_LABELS[state]
                : undefined
          }
          className={cn("shrink-0", gapText && "mt-0.5")}
        />
        {gapText ? <span className="min-w-0 break-words">{gapText}</span> : leadText(price)}
      </span>
      {origin && (
        <span className="text-fg-muted text-xs break-words whitespace-normal" data-lead-origin>
          {origin}
        </span>
      )}
      {ctx.longFigures && state === "known" && !fromItemMaster && price?.leadSource && (
        <span className="text-fg-muted text-xs break-words whitespace-normal">
          {leadTimeSentence(price.leadDays, price.leadSource, price.leadStatus)}
        </span>
      )}
      {aboutDays !== null && (ctx.longFigures || form === "card") && (
        // The source stands over it already: this line says what the customer reads.
        <span
          className="text-fg-muted text-xs break-words whitespace-normal"
          data-lead-about-in-reply
        >
          {LEAD_ORIGIN_WORDS.aboutInReply(aboutDays)}
        </span>
      )}
      {ctx.longFigures && state === "historic" && (
        <span className="text-fg-muted text-xs break-words whitespace-normal">
          {LEAD_ORIGIN_WORDS.contextOnly}
        </span>
      )}
    </span>
  );
}

/** The row's own button: Confirm for a suggestion, Pick item for an open line; null otherwise. */
export function RowActionButton({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const { line, word, lineIndex } = row;
  if (!canActOn(ctx, row)) {
    return null;
  }
  if (word === LINE_STATUS_WORDS.confirm && line.matchedItemId) {
    return (
      <Button
        size="sm"
        variant="outline"
        tone="accent"
        // Its own state only: while one row writes, the other rows stay ready (the
        // writes of a case run one after the other).
        loading={ctx.writes.decidingLineKey === line.lineKey}
        startIcon={<Icon icon={Check} size="xs" />}
        aria-label={`Confirm ${line.matchedItemId} for ${line.enquiryText}`}
        onClick={() => ctx.writes.decideLine(lineIndex, line.lineKey ?? "", { kind: "confirm" })}
        data-norican-grid-widget=""
      >
        {ACTION_LABELS.confirmLine}
      </Button>
    );
  }
  if (word === LINE_STATUS_WORDS.pick) {
    return (
      <Button
        size="sm"
        variant="outline"
        tone="accent"
        disabled={ctx.writes.busy}
        startIcon={<Icon icon={PackageSearch} size="xs" />}
        aria-label={`Pick an item for ${line.enquiryText}`}
        onClick={() => ctx.onPick(line.id)}
        data-norican-grid-widget=""
      >
        {ACTION_LABELS.pickItem}
      </Button>
    );
  }
  return null;
}

/** "Use this": a colleague's decision on the same line of the first mail (P18). */
export function TwinButton({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const twin = twinOf(row);
  if (!twin) {
    return null;
  }
  const { line, lineIndex } = row;
  return (
    <Button
      size="sm"
      variant="outline"
      tone="accent"
      disabled={ctx.writes.busy || !canActOn(ctx, row)}
      startIcon={<Icon icon={Check} size="xs" />}
      aria-label={`Use the decision of ${twin.case_id} for ${line.enquiryText}`}
      onClick={() =>
        ctx.writes.decideLine(
          lineIndex,
          line.lineKey ?? "",
          twinDecisionRequest(twin, line.matchedItemId),
        )
      }
      data-twin-decision
      data-norican-grid-widget=""
    >
      Use this
    </Button>
  );
}

/** The action cell of the table: one button per row (the twin decision first), then the row menu. */
export function ActionCell({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  return (
    <span className="flex items-center justify-end gap-1">
      {twinOf(row) ? <TwinButton row={row} ctx={ctx} /> : <RowActionButton row={row} ctx={ctx} />}
      <LineMenu row={row} ctx={ctx} />
    </span>
  );
}

/**
 * What is behind the chevron of a row: the details, then the row's extras. A twin
 * decision's sentence sits here with the row's own button (the cell shows "Use this");
 * a matched row gets "Change item", which opens the same item search as "Pick item".
 * The one place `DraftLineDetails` is rendered, for the table and the cards alike.
 */
export function DraftLineExtras({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const { line, word, lineIndex } = row;
  const twin = twinOf(row);
  const changeable = word === LINE_STATUS_WORDS.matched && !line.decision && canActOn(ctx, row);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <DraftLineDetails
        detail={ctx.detail}
        line={line}
        lineIndex={lineIndex}
        caseArea={ctx.caseArea}
        maySeeMargin={ctx.maySeeMargin}
        customerSuggested={ctx.customerSuggested && !ctx.customerToFind}
        priceNote={ctx.priceNote(line)}
        writes={ctx.writes}
        mayApprove={ctx.mayApprove}
      />
      {twin && (
        <div className="flex flex-wrap items-center gap-2">
          <Typography as="span" variant="caption" tone="muted" className="break-words">
            {twinDecisionSentence(twin, personName)}
          </Typography>
          <RowActionButton row={row} ctx={ctx} />
        </div>
      )}
      {changeable && (
        <div>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={ctx.writes.busy}
            startIcon={<Icon icon={PackageSearch} size="xs" />}
            aria-label={`Change the item for ${line.enquiryText}`}
            onClick={() => ctx.onPick(line.id)}
          >
            {DRAFT_CELL_WORDS.changeItem}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Whether a group row must say "Accept": the case is open and one of its rows is matched
 * by I2Q with no decision yet (the big button offers Accept for them).
 */
export function hasUndecidedMatched(
  ctx: Pick<DraftCellContext, "detail">,
  rows: readonly DraftRow[],
): boolean {
  const workOpen = (ctx.detail.workStatus ?? "open") === "open";
  return (
    workOpen && rows.some((row) => row.word === LINE_STATUS_WORDS.matched && !row.line.decision)
  );
}

/** The group row: its icon and one sentence (the DS prints the label and the count). */
export function GroupSentence({
  group,
  rows,
  ctx,
}: {
  group: DraftGroup;
  rows: readonly DraftRow[];
  ctx: Pick<DraftCellContext, "detail">;
}) {
  return (
    <span className="text-fg-muted inline-flex min-w-0 items-center gap-1.5 text-xs font-normal">
      <Icon icon={GROUP_ICON[group]} size="xs" tone={GROUP_TONE[group]} className="shrink-0" />
      <span className="min-w-0 truncate">
        {draftGroupSentence(group, hasUndecidedMatched(ctx, rows))}
      </span>
    </span>
  );
}

// ─── the columns ─────────────────────────────────────────────────────────────

/** The DataGrid column ids, for `hiddenColumnIds` and the tests. */
export const DRAFT_COLUMN_IDS = {
  status: "status",
  asked: "asked",
  proposed: "proposed",
  qty: "qty",
  price: "price",
  lead: "lead",
  action: "action",
} as const;

/**
 * The seven columns of the draft table. The fixed widths sum to 584 px; the two text
 * columns share what is left. `renderDetail` makes the status cell the drill button of
 * the row (the DS owns the open state, one row at a time).
 */
export function draftColumns(
  ctx: DraftCellContext,
  renderDetail: (row: DraftRow) => ReactNode,
): DataGridColumn<DraftRow>[] {
  return [
    {
      id: DRAFT_COLUMN_IDS.status,
      header: DRAFT_COLUMNS.status,
      width: 136,
      cell: (row) => <StatusBadge row={row} chevron />,
      renderCellDetail: (row) => renderDetail(row),
    },
    {
      id: DRAFT_COLUMN_IDS.asked,
      header: DRAFT_COLUMNS.asked,
      cell: (row) => <AskedCell row={row} ctx={ctx} />,
      aggregate: (rows, group) => (
        <GroupSentence group={group as DraftGroup} rows={rows} ctx={ctx} />
      ),
    },
    {
      id: DRAFT_COLUMN_IDS.proposed,
      header: DRAFT_COLUMNS.proposed,
      cell: (row) => <ProposedCell row={row} ctx={ctx} />,
    },
    {
      id: DRAFT_COLUMN_IDS.qty,
      header: DRAFT_COLUMNS.qty,
      width: 80,
      numeric: true,
      cell: (row) => <QuantityCell row={row} ctx={ctx} />,
      cellTone: (_value, row) => (qtyNeedsLook(row.line, row.word) ? "caution" : undefined),
    },
    {
      id: DRAFT_COLUMN_IDS.price,
      header: DRAFT_COLUMNS.price,
      width: 136,
      numeric: true,
      cell: (row) => <PriceCell row={row} ctx={ctx} />,
      cellTone: (_value, row) => {
        const band = priceBandOf(row.line.price, ctx.priceNote(row.line));
        if (band === "below_margin") {
          return row.line.price?.approval === "approved" ? undefined : "caution";
        }
        return band === "none" ? "muted" : undefined;
      },
    },
    {
      id: DRAFT_COLUMN_IDS.lead,
      header: DRAFT_COLUMNS.lead,
      width: 104,
      cell: (row) => <LeadCell row={row} ctx={ctx} />,
      cellTone: (_value, row) => (leadStateOf(row.line.price) === "known" ? undefined : "muted"),
    },
    {
      id: DRAFT_COLUMN_IDS.action,
      header: "",
      width: 128,
      pinned: "right",
      cell: (row) => <ActionCell row={row} ctx={ctx} />,
    },
  ];
}
